import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'src/components/image';
import { AuthArtworkSource } from './auth-artwork-content';

interface AuthArtworkProps extends AuthArtworkSource {
  onArtworkReady: () => void;
}

export default function AuthArtwork({
  src,
  previewSrc,
  onArtworkReady,
}: AuthArtworkProps): JSX.Element {
  const [fullImageReady, setFullImageReady] = useState(false);
  const readyCallback = useRef(onArtworkReady);
  const status = useRef({
    notified: false,
    fullFailed: false,
    previewFailed: false,
  });

  useEffect(() => {
    readyCallback.current = onArtworkReady;
  }, [onArtworkReady]);

  const notifyReady = useCallback((): void => {
    if (status.current.notified) return;
    status.current.notified = true;
    readyCallback.current();
  }, []);

  useEffect(() => {
    const preview = new window.Image();
    preview.onload = notifyReady;
    preview.onerror = (): void => {
      status.current.previewFailed = true;
      if (status.current.fullFailed) notifyReady();
    };
    preview.src = previewSrc;
    return () => {
      preview.onload = null;
      preview.onerror = null;
    };
  }, [notifyReady, previewSrc]);

  return (
    <Image
      src={src}
      alt=""
      disabledEffect
      visibleByDefault
      onLoad={(): void => {
        setFullImageReady(true);
        notifyReady();
      }}
      onError={(): void => {
        status.current.fullFailed = true;
        if (status.current.previewFailed) notifyReady();
      }}
      sx={{
        width: 1,
        height: 1,
        backgroundImage: `url("${previewSrc}")`,
        backgroundSize: 'contain',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat',
      }}
      imageSx={{
        objectFit: 'contain',
        objectPosition: 'center bottom',
        opacity: fullImageReady ? 1 : 0,
        transition: 'opacity 160ms ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    />
  );
}
